import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../lib/store";
import { api, fmtEur, fmtDate, errDetail } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  CalendarClock, AlertTriangle, AlertOctagon, Clock, RefreshCw, FileText, ChevronRight, Loader2,
} from "lucide-react";

const URGENCY = {
  notice_missed: { label: "urgency_notice_missed", color: "bg-red-100 text-red-700 border-red-200", icon: AlertOctagon, dot: "#dc2626" },
  notice_soon: { label: "urgency_notice_soon", color: "bg-orange-100 text-orange-700 border-orange-200", icon: AlertTriangle, dot: "#ea580c" },
  expired: { label: "urgency_expired", color: "bg-red-50 text-red-600 border-red-200", icon: AlertOctagon, dot: "#ef4444" },
  expiring_soon: { label: "urgency_expiring_soon", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock, dot: "#f5a623" },
  none: { label: "urgency_none", color: "bg-slate-50 text-slate-500 border-slate-200", icon: CalendarClock, dot: "#94a3b8" },
};

function StatCard({ icon: Icon, value, label, color, testid }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 flex items-center gap-3" data-testid={testid}>
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#1B2A4A] leading-none">{value}</p>
        <p className="text-xs text-gray-400 mt-1">{label}</p>
      </div>
    </div>
  );
}

export default function ContractDeadlines() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/contract-deadlines")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errDetail(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = data?.counts || {};
  const rows = data?.deadlines || [];

  return (
    <div className="space-y-6" data-testid="contract-deadlines-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>
            {t("contract_deadlines")}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{t("contract_deadlines_subtitle")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="deadlines-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />{t("refresh")}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={AlertOctagon} value={counts.notice_missed || 0} label={t("urgency_notice_missed")} color="bg-red-100 text-red-700" testid="stat-notice-missed" />
        <StatCard icon={AlertTriangle} value={counts.notice_soon || 0} label={t("urgency_notice_soon")} color="bg-orange-100 text-orange-700" testid="stat-notice-soon" />
        <StatCard icon={Clock} value={counts.expiring_soon || 0} label={t("urgency_expiring_soon")} color="bg-amber-100 text-amber-700" testid="stat-expiring-soon" />
        <StatCard icon={AlertOctagon} value={counts.expired || 0} label={t("urgency_expired")} color="bg-red-50 text-red-600" testid="stat-expired" />
        <StatCard icon={FileText} value={`${data?.with_documents || 0}/${data?.total || 0}`} label={t("with_ai_analysis")} color="bg-[#1B2A4A]/10 text-[#1B2A4A]" testid="stat-with-docs" />
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center rounded-xl border border-gray-200 bg-white" data-testid="deadlines-empty">
          <CalendarClock className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="font-medium text-gray-700">{t("no_deadlines")}</p>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                  <th className="px-4 py-2.5 font-semibold">{t("urgency")}</th>
                  <th className="px-4 py-2.5 font-semibold">{t("contract")}</th>
                  <th className="px-4 py-2.5 font-semibold">{t("account")}</th>
                  <th className="px-4 py-2.5 font-semibold text-right">{t("end_date")}</th>
                  <th className="px-4 py-2.5 font-semibold text-right">{t("notice_deadline")}</th>
                  <th className="px-4 py-2.5 font-semibold text-right">{t("annual_value")}</th>
                  <th className="px-4 py-2.5 font-semibold">{t("auto_renewal")}</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const u = URGENCY[r.urgency] || URGENCY.none;
                  const UIcon = u.icon;
                  return (
                    <tr key={r.contract_id}
                      className="border-b border-gray-100 hover:bg-amber-50/40 cursor-pointer transition-colors"
                      onClick={() => navigate(`/accounts/${r.account_id}`)} data-testid={`deadline-row-${r.contract_number}`}>
                      <td className="px-4 py-2.5">
                        <Badge className={`border ${u.color} hover:bg-inherit`} data-testid={`urgency-badge-${r.contract_number}`}>
                          <UIcon className="h-3 w-3 mr-1" />{t(u.label)}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-[#1B2A4A]">{r.name}</p>
                        <p className="font-mono text-[11px] text-gray-400">{r.contract_number}
                          {r.has_document && <FileText className="inline h-3 w-3 ml-1 text-[#1B2A4A]/50" />}
                        </p>
                      </td>
                      <td className="px-4 py-2.5 text-gray-700">{r.account_name}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="font-mono">{fmtDate(r.end_date)}</span>
                        {r.days_to_expiry != null && (
                          <span className={`block text-[11px] ${r.days_to_expiry < 0 ? "text-red-600" : r.days_to_expiry <= 90 ? "text-amber-600" : "text-gray-400"}`}>
                            {r.days_to_expiry} {t("days")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {r.notice_deadline ? (
                          <>
                            <span className="font-mono">{fmtDate(r.notice_deadline)}</span>
                            <span className={`block text-[11px] ${r.days_to_notice < 0 ? "text-red-600 font-semibold" : r.days_to_notice <= 30 ? "text-orange-600" : "text-gray-400"}`}>
                              {r.days_to_notice < 0 ? t("overdue") : `${r.days_to_notice} ${t("days")}`}
                              {r.notice_months ? ` · ${r.notice_months} ${t("months")}` : ""}
                            </span>
                          </>
                        ) : (
                          <span className="text-xs text-gray-300" title={t("no_notice_detected")}>
                            {r.has_document ? "–" : t("upload_for_notice")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono">{fmtEur(r.annual_value)}</td>
                      <td className="px-4 py-2.5">
                        {r.has_document ? (
                          <Badge className={r.auto_renewal ? "bg-blue-50 text-blue-700 hover:bg-blue-50" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}>
                            {r.auto_renewal ? t("yes") : t("no")}
                          </Badge>
                        ) : <span className="text-gray-300">–</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right"><ChevronRight className="h-4 w-4 text-gray-300" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
